import {
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
} from "@mui/material";
import Alert from "@mui/material/Alert";
import Field from "components/Field";
import { FORM_ERROR } from "final-form";
import useGlobalContext from "hooks/globalData";
import { pick } from "lodash";
import { useCallback, useMemo } from "react";
import { FormProps, withTypes } from "react-final-form";
import { useIntl } from "react-intl";
import {
  ILicensureBodyDetail,
  LicensureBodyRequest,
} from "services/KnowfullyAdminClient";
import translateError from "utils/translateError";

const { Form } = withTypes<LicensureBodyRequest>();

type LicensureBodyFormProps = {
  licensureBodyId: string;
  licensureBody?: ILicensureBodyDetail;
  title: string;
  onCancel: () => void;
  onSubmit: () => void;
};

const LicensureBodyForm = ({
  licensureBodyId,
  licensureBody,
  title,
  onCancel,
  onSubmit,
}: LicensureBodyFormProps & FormProps<LicensureBodyRequest>) => {
  const { formatMessage: t } = useIntl();
  const { licensureBodyService } = useGlobalContext();

  const init = useMemo(
    () => pick(licensureBody ?? {}, ["name", "acronym", "description"]),
    [licensureBody],
  );

  const handleSubmit = useCallback(
    async (values: LicensureBodyRequest) => {
      try {
        await licensureBodyService.updateLicensureBody(licensureBodyId, values);
        onSubmit();
      } catch (error) {
        return { [FORM_ERROR]: translateError(error, t) };
      }
    },
    [licensureBodyId, licensureBodyService, onSubmit, t],
  );

  return (
    <Form
      onSubmit={handleSubmit}
      initialValues={init}
      render={({ handleSubmit, submitting, submitError }) => (
        <form onSubmit={handleSubmit}>
          <DialogTitle id="form-dialog-title">{title}</DialogTitle>
          <DialogContent dividers>
            <Grid container spacing={2}>
              <Field name="name" required>
                {({ input, meta }) => (
                  <Grid item xs={12}>
                    <TextField
                      variant="standard"
                      fullWidth
                      label={t({ id: "nameLabel" })}
                      InputLabelProps={{ required: true }}
                      error={meta.error && meta.submitFailed}
                      helperText={meta.error && meta.submitFailed && meta.error}
                      {...input}
                    />
                  </Grid>
                )}
              </Field>

              <Field name="acronym" required>
                {({ input, meta }) => (
                  <Grid item xs={12}>
                    <TextField
                      variant="standard"
                      fullWidth
                      label={t({ id: "acronymLabel" })}
                      InputLabelProps={{ required: true }}
                      error={meta.error && meta.submitFailed}
                      helperText={meta.error && meta.submitFailed && meta.error}
                      {...input}
                    />
                  </Grid>
                )}
              </Field>

              <Field name="description" required>
                {({ input, meta }) => (
                  <Grid item xs={12}>
                    <TextField
                      variant="standard"
                      fullWidth
                      minRows={3}
                      label={t({ id: "descriptionLabel" })}
                      InputLabelProps={{ required: true }}
                      error={meta.error && meta.submitFailed}
                      helperText={meta.error && meta.submitFailed && meta.error}
                      {...input}
                    />
                  </Grid>
                )}
              </Field>

              {submitError && (
                <Grid item xs={12}>
                  <Alert severity="error">{submitError}</Alert>
                </Grid>
              )}
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button disabled={submitting} onClick={onCancel}>
              {t({ id: "cancelButton" })}
            </Button>
            <Button
              type="submit"
              color="primary"
              disabled={submitting}
              startIcon={submitting && <CircularProgress size={16} />}
            >
              {t({ id: "saveButton" })}
            </Button>
          </DialogActions>
        </form>
      )}
    />
  );
};

export default LicensureBodyForm;
